import { AuthStatus, BudgetApi, PaymentDefinedRange, PayType, UnitUsageRange } from "@asd/customer-portal-api";
import { AxiosResponse } from "axios";

import AxiosConfigurations, { AxiosConfiguration } from "./AxiosConfiguration";

import { axiosInstance } from ".";

export class BudgetService {
  static instance: BudgetService;
  private budgetApi;
  private axiosConfigurationAuths: AxiosConfiguration | undefined;
  private axiosConfigurationClaims: AxiosConfiguration | undefined;

  constructor() {
    if (BudgetService.instance) {
      return BudgetService.instance;
    }

    this.budgetApi = new BudgetApi(undefined, undefined, axiosInstance);

    const authsConfig = AxiosConfigurations.createAxiosConfiguration("budgetServiceAuths", this.budgetApi);
    if (!authsConfig) {
      throw new Error("Failed to create AxiosConfiguration for budgetServiceAuths");
    }
    const claimsConfig = AxiosConfigurations.createAxiosConfiguration("budgetServiceClaims", this.budgetApi);
    if (!claimsConfig) {
      throw new Error("Failed to create AxiosConfiguration for budgetServiceClaims");
    }
    this.axiosConfigurationAuths = authsConfig;
    this.axiosConfigurationClaims = claimsConfig;

    BudgetService.instance = this;
  }

  public getPendingRequestCount(type: string) {
    return (type === "auths" ? this.axiosConfigurationAuths : this.axiosConfigurationClaims)?.getPendingRequestCount();
  }

  public async getAuthorizations(
    participantId?: string,
    description?: string,
    statuses?: AuthStatus[],
    unitUsageRanges?: UnitUsageRange[],
    page?: number,
    size?: number,
    sort?: Array<string>,
    options: object = {},
  ) {
    if (!this.budgetApi) {
      throw new Error("budgetApi is not initialized");
    }

    try {
      const response: AxiosResponse = await this.budgetApi.getAuthorizations(
        participantId?.length ? participantId : undefined,
        description,
        statuses?.length ? statuses : undefined,
        unitUsageRanges?.length ? unitUsageRanges : undefined,
        page,
        size,
        sort?.length ? sort : undefined,
        options,
      );
      return response;
    } catch {
      // do nothing
    }
  }

  public async exportAuthorizations(
    participantId?: string,
    description?: string,
    statuses?: AuthStatus[],
    unitUsageRanges?: UnitUsageRange[],
    sort?: Array<string>,
    options: object = {},
  ) {
    if (!this.budgetApi) {
      throw new Error("budgetApi is not initialized");
    }

    try {
      const response: AxiosResponse = await this.budgetApi.exportAuthorizationsCsv(
        participantId?.length ? participantId : undefined,
        description,
        statuses?.length ? statuses : undefined,
        unitUsageRanges?.length ? unitUsageRanges : undefined,
        sort?.length ? sort : undefined,
        options,
      );

      return response.data;
    } catch {
      // do nothing
    }
  }

  public async getPayments(
    participantId?: string,
    authorizationServiceIds?: string[],
    authorizationIds?: string[],
    providerName?: string,
    serviceDate?: PaymentDefinedRange,
    payTypes?: PayType[],
    page?: number,
    size?: number,
    sort?: Array<string>,
    options: object = {},
  ) {
    if (!this.budgetApi) {
      throw new Error("budgetApi is not initialized");
    }

    try {
      const response: AxiosResponse = await this.budgetApi.getPayments(
        participantId?.length ? participantId : undefined,
        authorizationServiceIds?.length ? authorizationServiceIds : undefined,
        authorizationIds?.length ? authorizationIds : undefined,
        providerName?.length ? providerName : undefined,
        serviceDate,
        payTypes,
        page,
        size,
        sort?.length ? sort : undefined,
        options,
      );
      return response;
    } catch {
      // do nothing
    }
  }

  public async exportPayments(
    participantId?: string,
    authorizationServiceIds?: string[],
    authorizationIds?: string[],
    providerName?: string,
    serviceDate?: PaymentDefinedRange,
    payTypes?: PayType[],
    sort?: Array<string>,
    options: object = {},
  ) {
    if (!this.budgetApi) {
      throw new Error("budgetApi is not initialized");
    }

    try {
      const response: AxiosResponse = await this.budgetApi.exportPaymentsCsv(
        participantId?.length ? participantId : undefined,
        authorizationServiceIds?.length ? authorizationServiceIds : undefined,
        authorizationIds?.length ? authorizationIds : undefined,
        providerName?.length ? providerName : undefined,
        serviceDate,
        payTypes,
        sort?.length ? sort : undefined,
        options,
      );

      return response.data;
    } catch {
      // do nothing
    }
  }
}

export default new BudgetService();
